import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  TrendingUp,
  ArrowRight,
  DollarSign,
  CheckCircle,
  Clock,
  Calendar,
  Loader2,
  Megaphone,
  AlertCircle,
  Info,
  Pin,
  ExternalLink,
  Upload,
  Share2,
  Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { companyRegistrationApi, directoryApi, quotesApi, eventsApi, profileApi, notificationsApi, analyticsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { getTierColor } from '../utils/tierColors';
import { toast } from 'sonner';
import axios from 'axios';
import ProfilePictureUploadDialog from './ProfilePictureUploadDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const QUOTES_PAGE_SIZE = 200;

const firstText = (...values) => values.find((v) => typeof v === 'string' && v.trim() !== '') || '';
const hasProfilePictureValue = (value) => typeof value === 'string' && value.trim() !== '';

const formatPortDisplay = (name, code) => {
  if (!code && !name) return 'N/A';
  const n = (name || '').trim();
  const c = (code || '').trim();
  if (n && c && (n.endsWith(`(${c})`) || n === c)) return n;
  if (n && c && n !== c) return `${n} (${c})`;
  return n || c || 'N/A';
};

const buildDashboardRouteDisplay = (quote, airports = []) => {
  const fd = quote?.form_data || {};
  const quoteType = quote?.quote_type;
  const isAirQuote = quoteType === 'Air';

  const formatAirportDisplay = (value) => {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw) return '';
    const airport = airports.find((item) => (item?.code || '').toUpperCase() === raw.toUpperCase());
    if (!airport) return raw;

    const code = (airport.code || raw).trim();
    const name = (airport.name || '').trim();
    const nameAndLocode = (airport.nameAndLocode || '').trim();

    if (nameAndLocode && !nameAndLocode.includes('(')) return `${nameAndLocode} (${code})`;
    if (nameAndLocode) return nameAndLocode;
    if (name && name !== code) return `${name} (${code})`;
    return code;
  };

  if (isAirQuote) {
    const origin = formatAirportDisplay(firstText(fd?.origin, quote?.origin_city));
    const destination = formatAirportDisplay(firstText(fd?.destination, quote?.destination_city));
    return [origin || 'N/A', destination || 'N/A'];
  }

  const origin = formatPortDisplay(quote?.origin_port?.name, quote?.origin_port?.code) !== 'N/A'
    ? formatPortDisplay(quote?.origin_port?.name, quote?.origin_port?.code)
    : firstText(fd?.origin?.port, fd?.polName, quote?.origin_city) || 'N/A';
  const destination = formatPortDisplay(quote?.destination_port?.name, quote?.destination_port?.code) !== 'N/A'
    ? formatPortDisplay(quote?.destination_port?.name, quote?.destination_port?.code)
    : firstText(fd?.destination?.port, fd?.podName, quote?.destination_city) || 'N/A';
  return [origin, destination];
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [memberProfile, setMemberProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [airports, setAirports] = useState([]);
  const [quoteMetrics, setQuoteMetrics] = useState({
    sentCount: 0,
    nominatedCount: 0,
    dealSize: 0,
    dealSizeCount: 0,
  });
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(true);
  const [showProfilePictureDialog, setShowProfilePictureDialog] = useState(false);
  const [showLinkedInConsentDialog, setShowLinkedInConsentDialog] = useState(false);
  const [savingLinkedInConsent, setSavingLinkedInConsent] = useState(false);
  const navigate = useNavigate();

  const { member, user } = useAuth();
  const { hasFeatureAccess } = useTier();
  const currentMemberId = member?.member_id;
  const canAccessQuotes = hasFeatureAccess('quotes');
  const isCompanyAdmin = member?.has_admin_rights === true;
  const isRegisteredUserTier = ((memberProfile?.tier || member?.tier || '')).toLowerCase() === 'registered user';
  const hasRegistrationEvidenceFromMember =
    Boolean(memberProfile?.registration_id || member?.registration_id) &&
    Boolean(memberProfile?.validated_at || member?.validated_at);
  const isCompanyRegistrationApproved =
    (companyRegistration?.status || '').trim().toLowerCase() === 'approved' ||
    hasRegistrationEvidenceFromMember;
  const hasProfilePicture =
    hasProfilePictureValue(memberProfile?.profile_picture) ||
    hasProfilePictureValue(member?.profile_picture);
  const hasCompanyLogo = Boolean(
    companyRegistration?.company_info?.company_logo ||
    memberProfile?.company_logo ||
    member?.company_logo
  );
  const hasLinkedInOnboardingConsent =
    typeof memberProfile?.linkedin_onboarding_consent === 'boolean' ||
    typeof member?.linkedin_onboarding_consent === 'boolean';
  const shouldAskForLinkedInOnboardingConsent =
    !loading &&
    !registrationLoading &&
    (hasProfilePicture || hasCompanyLogo) &&
    !hasLinkedInOnboardingConsent;

  useEffect(() => {
    fetchDashboardData();
    fetchAnnouncements();
    fetchCompanyRegistration();
  }, [currentMemberId, user]);

  useEffect(() => {
    if (shouldAskForLinkedInOnboardingConsent) {
      setShowLinkedInConsentDialog(true);
    }
  }, [shouldAskForLinkedInOnboardingConsent]);

  const fetchAnnouncements = async () => {
    try {
      const tier = member?.tier || 'Silver';
      const country = member?.country;
      const response = await axios.get(`${API_URL}/api/announcements/`, {
        params: { member_tier: tier, member_country: country, limit: 5 }
      });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const fetchCompanyRegistration = async () => {
    try {
      setRegistrationLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setRegistrationLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/company-registration/my-registration`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.exists) {
        setCompanyRegistration(response.data);
      }
    } catch (error) {
      // If 404 or no registration, that's fine - user hasn't submitted yet
      if (error.response?.status !== 404) {
        console.error('Failed to fetch company registration:', error);
      }
    } finally {
      setRegistrationLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    if (!currentMemberId) return;
    try {
      setLoading(true);
      const [profileRes, membersRes, sentQuotes, receivedQuotes, eventsRes, notifRes, overviewRes, airportsRes] = await Promise.all([
        profileApi.getProfile(currentMemberId),
        directoryApi.getMembers({ limit: 10 }),
        fetchAllQuotes((skip) =>
          quotesApi.getQuotes({
            requester_id: currentMemberId,
            limit: QUOTES_PAGE_SIZE,
            skip,
          })
        ),
        fetchAllQuotes((skip) =>
          quotesApi.getAvailableQuotes(currentMemberId, {
            limit: QUOTES_PAGE_SIZE,
            skip,
          })
        ),
        eventsApi.getUpcomingEvents(),
        notificationsApi.getNotifications(currentMemberId, { limit: 5 }).catch(() => ({ data: [] })),
        // Use company-level analytics so dashboard matches Analytics "Company" totals.
        analyticsApi.getCompanyOverview(currentMemberId).catch(() => ({ data: { overview: null } })),
        directoryApi.getAirports().catch(() => ({ data: [] }))
      ]);

      setMemberProfile(profileRes.data);
      setMembers(membersRes.data || []);
      setQuotes(sentQuotes);
      setAirports(airportsRes?.data || []);
      setEvents(eventsRes.data || []);
      setAnalyticsOverview(overviewRes?.data?.overview ?? null);

      const wonQuotes = receivedQuotes.filter(
        (quote) => quote.my_bid_nominated && (quote.status === 'Nominated' || quote.status === 'Completed')
      );
      const dealSize = await getWonQuotesDealSizeTotal(wonQuotes);
      setQuoteMetrics({
        sentCount: sentQuotes.length,
        nominatedCount: wonQuotes.filter((quote) => quote.status === 'Nominated').length,
        dealSize,
        dealSizeCount: wonQuotes.length,
      });

      const notifs = notifRes?.data || [];
      setRecentActivities(notifs.map((n, i) => ({
        id: n.notification_id || n.id || i,
        type: (n.type || '').includes('quote') || (n.type || '').includes('bid') ? 'quote' : (n.type || '').includes('event') ? 'event' : 'member',
        message: n.title || n.message || 'Notification',
        timestamp: n.created_at ? formatActivityTime(n.created_at) : ''
      })));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatActivityTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return d.toLocaleDateString();
  };

  const fetchAllQuotes = async (fetchPage) => {
    const allQuotes = [];
    let skip = 0;

    while (true) {
      const response = await fetchPage(skip);
      const page = Array.isArray(response?.data) ? response.data : [];
      allQuotes.push(...page);

      if (page.length < QUOTES_PAGE_SIZE) {
        break;
      }
      skip += QUOTES_PAGE_SIZE;
    }

    return allQuotes;
  };

  const calculateLineItemsTotal = (items = []) => {
    return (items || []).reduce((sum, item) => {
      const quantity = Number(item?.quantity || 1);
      const unitPrice = Number(item?.unit_price || 0);
      const lineTotal = unitPrice * quantity;
      const taxAmount = item?.tax_applicable && item?.tax_rate
        ? lineTotal * (Number(item.tax_rate) / 100)
        : 0;
      return sum + lineTotal + taxAmount;
    }, 0);
  };

  const getWonQuotesDealSizeTotal = async (wonQuotes) => {
    const amounts = await Promise.all(
      wonQuotes.map(async (quote) => {
        const fallbackAmount = Number(quote?.payment_details?.amount || 0);

        try {
          const response = await quotesApi.getQuote(quote.quote_id);
          const bids = response?.data?.bids || [];
          const nominatedBid =
            bids.find((bid) => bid.bid_id === quote.nominated_bid_id) ||
            bids.find((bid) => bid.bid_id === quote.my_bid_id) ||
            bids.find((bid) => bid.bidder_id === currentMemberId && bid.is_nominated);
          const lineItemsTotal = calculateLineItemsTotal(nominatedBid?.line_items);
          const resolvedAmount =
            lineItemsTotal > 0
              ? lineItemsTotal
              : (nominatedBid?.total ?? nominatedBid?.total_amount ?? fallbackAmount);

          return Number(resolvedAmount) || 0;
        } catch (error) {
          console.error('Failed to fetch won quote total:', error);
          return fallbackAmount;
        }
      })
    );

    return amounts.reduce((sum, amount) => sum + amount, 0);
  };

  const stats = [
    {
      label: 'Total Quotes',
      value: analyticsOverview?.total_quotes ?? (quoteMetrics.sentCount + quoteMetrics.nominatedCount),
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/quotes'
    },
    {
      label: 'Deal Size',
      value: `$${quoteMetrics.dealSize.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`,
      subtitle: quoteMetrics.dealSizeCount != null
        ? `From ${quoteMetrics.dealSizeCount.toLocaleString()} quote${quoteMetrics.dealSizeCount === 1 ? '' : 's'}`
        : null,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/quotes'
    },
    {
      label: 'Active Deals',
      value: analyticsOverview?.active_deals ?? quoteMetrics.nominatedCount,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      link: '/quotes'
    },
    {
      label: 'Network Partners Connected',
      value: (memberProfile?.network_partner_connections ?? analyticsOverview?.network_connections ?? 0).toLocaleString(),
      icon: Users,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      link: '/directory'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  const tierColor = getTierColor(memberProfile?.tier || 'Platinum');
  const memberRating = Number(memberProfile?.member_rating) > 0
    ? Number(memberProfile.member_rating)
    : 5;

  const renderPartialStars = (rating) => (
    <div className="mt-2 flex justify-start gap-1 sm:justify-end">
      {Array.from({ length: 5 }, (_, index) => {
        const fillPercent = Math.max(0, Math.min(1, rating - index)) * 100;

        return (
          <div key={index} className="relative h-7 w-7">
            <Star className="h-7 w-7 text-blue-200/70" />
            {fillPercent > 0 && (
              <div
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${fillPercent}%` }}
              >
                <Star className="h-7 w-7 fill-yellow-500 text-yellow-500" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Only the company admin (registrant) should be forced to submit/manage
  // company registration documents. Invited team members can use the platform.
  const needsVerificationUpload =
    isCompanyAdmin &&
    !registrationLoading &&
    !companyRegistration &&
    !hasRegistrationEvidenceFromMember;
  const needsMembershipTierSelection =
    isCompanyAdmin &&
    !registrationLoading &&
    isCompanyRegistrationApproved &&
    isRegisteredUserTier;
  const handleUploadDocumentsForVerification = () => {
    navigate('/company-registration', {
      state: {
        prefillData: {
          company_name: memberProfile?.company_name || member?.company_name || user?.company_name || '',
          primary_contact_name: member?.individual_name || user?.full_name || '',
          primary_contact_email: member?.email || user?.email || '',
          country_of_registration: member?.country || user?.country || '',
          country: member?.country || user?.country || '',
          city: member?.city || user?.city || ''
        }
      }
    });
  };
  const handleSelectMembershipTier = () => {
    navigate('/upgrade');
  };

  const handleLinkedInOnboardingConsent = async (consent) => {
    try {
      setSavingLinkedInConsent(true);
      await companyRegistrationApi.updateLinkedinOnboardingConsent(consent);
      setMemberProfile((current) => current ? {
        ...current,
        linkedin_onboarding_consent: consent,
      } : current);
      setShowLinkedInConsentDialog(false);
      toast.success(consent ? 'Consent saved. Thank you.' : 'Your preference has been saved.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save your preference. Please try again.');
    } finally {
      setSavingLinkedInConsent(false);
    }
  };


  return (
    <div className="space-y-6" data-testid="dashboard">
      {!hasProfilePicture ? (
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 ${needsVerificationUpload ? 'relative z-[45] shadow-lg' : ''}`}>
          <div>
            <p className="font-medium text-blue-950">Complete your personal profile</p>
            <p className="text-sm text-blue-700">Add a profile picture so other members can recognize you.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="border-blue-300 bg-white text-blue-700 hover:bg-blue-100"
            onClick={() => setShowProfilePictureDialog(true)}
          >
            <Upload size={16} className="mr-2" />
            Upload Profile Picture
          </Button>
        </div>
      ) : null}
      <ProfilePictureUploadDialog
        open={showProfilePictureDialog}
        onOpenChange={setShowProfilePictureDialog}
        onUploaded={(profilePicture) => {
          setMemberProfile((current) => current ? { ...current, profile_picture: profilePicture } : current);
        }}
      />
      <Dialog
        open={showLinkedInConsentDialog}
        onOpenChange={(open) => {
          if (!savingLinkedInConsent) setShowLinkedInConsentDialog(open);
        }}
      >
        <DialogContent className="max-w-md" onPointerDownOutside={(event) => event.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Share your onboarding announcement?</DialogTitle>
            <DialogDescription>
              Your profile or company image is already on file. Do you consent to One Globe Alliance sharing your onboarding announcement on its LinkedIn page?
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This is optional. Your choice is saved only for your member profile and will not affect membership access.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={savingLinkedInConsent}
              onClick={() => handleLinkedInOnboardingConsent(false)}
            >
              No, do not share
            </Button>
            <Button
              type="button"
              disabled={savingLinkedInConsent}
              onClick={() => handleLinkedInOnboardingConsent(true)}
            >
              {savingLinkedInConsent ? 'Saving...' : 'I consent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {needsVerificationUpload && (
        <div
          className="fixed inset-0 z-40 cursor-not-allowed bg-slate-900/10 backdrop-blur-[1px]"
          aria-hidden="true"
        />
      )}

      {/* Welcome Section */}
      <div className={`rounded-2xl bg-gradient-to-r from-blue-600 to-blue-800 p-4 text-white sm:p-6 ${needsVerificationUpload ? 'relative z-[45] shadow-2xl ring-4 ring-amber-200/80' : ''}`}>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold">Welcome back, {memberProfile?.individual_name || 'Member'}!</h1>
            <p className="text-blue-100 mt-1">{memberProfile?.company_name}</p>
            <div className="flex items-center space-x-3 mt-3">
              <Badge className={`${tierColor.badge} border`}>
                {memberProfile?.tier} Member
              </Badge>
            </div>
            {registrationLoading ? (
              <div className="mt-4 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-blue-100">Loading registration status...</span>
              </div>
            ) : needsVerificationUpload ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Badge className="border border-amber-200 bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
                  Next step
                </Badge>
                <Button
                  variant="default"
                  className="w-full bg-amber-300 px-5 py-5 text-base font-bold text-blue-950 shadow-lg ring-2 ring-white/70 hover:bg-amber-200 sm:w-auto"
                  onClick={handleUploadDocumentsForVerification}
                >
                  <Upload size={18} className="mr-2" />
                  Upload Documents for Verification
                </Button>
              </div>
            ) : needsMembershipTierSelection ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Badge className="border border-amber-200 bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
                  Next step
                </Badge>
                <Button
                  variant="default"
                  className="w-full bg-amber-300 px-5 py-5 text-base font-bold text-blue-950 shadow-lg ring-2 ring-white/70 hover:bg-amber-200 sm:w-auto"
                  onClick={handleSelectMembershipTier}
                >
                  <TrendingUp size={18} className="mr-2" />
                  Select Membership Tier
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="default"
                  className="bg-white text-blue-600 hover:bg-blue-50 font-medium"
                  onClick={() => navigate('/referrals')}
                >
                  <Share2 size={16} className="mr-2" />
                  Refer Partner
                </Button>
              </div>
            )}
          </div>
          <div className="text-left sm:text-right">
            <p className="text-sm text-blue-100">Rating</p>
            {renderPartialStars(memberRating)}
            <p className="mt-2 text-lg font-semibold">{memberRating.toFixed(1)} / 5.0</p>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          className={needsVerificationUpload ? 'pointer-events-none select-none opacity-35 blur-[2px] transition duration-200' : ''}
          aria-hidden={needsVerificationUpload ? 'true' : undefined}
        >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link} className="block h-full">
              <Card className="h-full min-h-[168px] cursor-pointer transition-shadow hover:shadow-md">
                <CardContent className="flex h-full p-6">
                  <div className="flex h-full w-full items-start justify-between gap-4">
                    <div className="flex min-h-[96px] flex-1 flex-col justify-between">
                      <p className="text-sm text-gray-600">{stat.label}</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{stat.value}</p>
                      {stat.subtitle && (
                        <p className="mt-1 text-xs text-gray-500">{stat.subtitle}</p>
                      )}
                    </div>
                    <div className={`${stat.bgColor} rounded-lg p-3 self-start shrink-0`}>
                      <Icon size={24} className={stat.color} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Quotes */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Recent Quotes</CardTitle>
            <Link to="/quotes">
              <Button variant="ghost" size="sm">
                View All <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No quotes yet</p>
            ) : (
              <div className="space-y-3">
                {quotes.slice(0, 5).map((quote) => {
                  const [originLabel, destinationLabel] = buildDashboardRouteDisplay(quote, airports);
                  return (
                  <div key={quote.quote_id} className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center space-x-3">
                      <div className={`p-2 rounded-lg ${quote.status === 'Active' ? 'bg-green-100' :
                        quote.status === 'Pending' ? 'bg-yellow-100' :
                          'bg-gray-100'
                        }`}>
                        <FileText size={16} className={
                          quote.status === 'Active' ? 'text-green-600' :
                            quote.status === 'Pending' ? 'text-yellow-600' :
                              'text-gray-600'
                        } />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900">
                          {quote.quote_type} - {quote.commodity || 'General'}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {originLabel} to {destinationLabel}
                        </p>
                      </div>
                    </div>
                    <div className="self-start text-left sm:self-auto sm:text-right">
                      <Badge className={
                        quote.status === 'Active' ? 'bg-green-100 text-green-700' :
                          quote.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                            quote.status === 'Completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                      }>
                        {quote.status}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {quote.responses_count || 0} responses
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Announcements */}
          {announcements.length > 0 && (
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center">
                  <Megaphone size={18} className="mr-2 text-blue-600" />
                  Announcements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {announcements.slice(0, 3).map((ann) => (
                    <div key={ann.announcement_id} className="border-b last:border-0 pb-3 last:pb-0">
                      <div className="flex items-start space-x-2">
                        {ann.is_pinned && <Pin size={12} className="text-amber-500 mt-1 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <Badge className={
                              ann.announcement_type === 'important' ? 'bg-red-100 text-red-700' :
                                ann.announcement_type === 'event' ? 'bg-purple-100 text-purple-700' :
                                  'bg-blue-100 text-blue-700'
                            } style={{ fontSize: '10px', padding: '2px 6px' }}>
                              {ann.announcement_type === 'important' && <AlertCircle size={10} className="mr-1" />}
                              {ann.announcement_type}
                            </Badge>
                          </div>
                          <p className="font-medium text-sm text-gray-900 mt-1">{ann.title}</p>
                          <p className="text-xs text-gray-600 line-clamp-2 mt-1">{ann.content}</p>
                          {ann.link_url && (
                            <a
                              href={ann.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center mt-1"
                            >
                              {ann.link_text || 'Learn More'} <ExternalLink size={10} className="ml-1" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Events */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Upcoming Events</CardTitle>
              <Link to="/event-connect">
                <Button variant="ghost" size="sm">
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-gray-500 text-center py-2 text-sm">No upcoming events</p>
              ) : (
                <div className="space-y-3">
                  {events.slice(0, 3).map((event) => (
                    <div key={event.event_id} className="flex items-start space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Calendar size={16} className="text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">{event.title}</p>
                        <p className="text-xs text-gray-500">{event.location}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(event.start_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Announcements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Announcements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivities.length === 0 ? (
                  <p className="text-sm text-gray-500">No announcements</p>
                ) : (
                  recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`p-1.5 rounded-full ${activity.type === 'quote' ? 'bg-blue-100' :
                        activity.type === 'member' ? 'bg-purple-100' :
                          'bg-green-100'
                        }`}>
                        {activity.type === 'quote' ? (
                          <FileText size={12} className="text-blue-600" />
                        ) : activity.type === 'member' ? (
                          <Users size={12} className="text-purple-600" />
                        ) : (
                          <Calendar size={12} className="text-green-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {canAccessQuotes ? (
                <Link to="/quotes" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText size={16} className="mr-2" />
                    Create New Quote
                  </Button>
                </Link>
              ) : (
                <Button variant="outline" className="w-full justify-start cursor-default" disabled>
                  <FileText size={16} className="mr-2" />
                  Create New Quote
                </Button>
              )}
              <Link to="/directory" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Users size={16} className="mr-2" />
                  Find Partners
                </Button>
              </Link>
              <Link to="/event-connect" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Calendar size={16} className="mr-2" />
                  Upcoming Events
                </Button>
              </Link>
              <Link to="/share-social" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Share2 size={16} className="mr-2" />
                  Post on LinkedIn
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
        </div>
        {needsVerificationUpload && (
          <div
            className="absolute inset-0 z-10 cursor-not-allowed rounded-lg"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
